import React, { useRef, useState } from 'react';
import {
  useRecoilState,
  useRecoilValue,
  useSetRecoilState
} from 'recoil';
import { Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { appConfigState } from 'features/optimization/state/appConfigAtoms';
import { cfdConfigState } from 'features/optimization/state/cfdConfigAtoms';
import { yamlConfigState } from 'features/optimization/state/yamlConfigAtoms';
import { exportFile } from 'features/optimization/utils/exportFile';
import { importFile } from 'features/optimization/utils/importFile';
import { parameterListState } from 'features/morphing/state/parameterAtoms';
import { fileNameState } from 'features/morphing/state/stageParameterAtoms';
import { allModelsState, displayModelState } from 'features/file-browser/state/fileAtoms';
import { CustomTextField } from 'shared/ui/CustomTextField';
import { CustomButton } from 'shared/ui/CustomButton';
import { YamlEditor } from './YamlEditor';

const formContainerSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '90%',
  paddingTop: 1,
};

export const ConfigFileManager: React.FC = () => {
  const setAppConfig = useSetRecoilState(appConfigState);
  const setCFDConfig = useSetRecoilState(cfdConfigState);
  const setParameterList = useSetRecoilState(parameterListState);
  const setDisplayModel = useSetRecoilState(displayModelState);
  const [fileName, setFileName] = useRecoilState(fileNameState)
  const allModels = useRecoilValue(allModelsState);
  const yamlConfig = useRecoilValue(yamlConfigState);

  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ダイアログを開く前にフォーカスを外してaria-hidden警告を抑制する
  const handleOpenEditor = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    setOpen(true);
  };

  const onExportClick = async () => {
    await exportFile(
      fileName,
      yamlConfig
    );
  };

  const onImportClick = async () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Box component="div" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, paddingBottom: 0.5 }}>
      <Box component="div" sx={formContainerSx}>
        <CustomTextField
          required
          label='config file name'
          type='text'
          variant='outlined'
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          sx={{
            width: '80%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: "#404040",
            },
          }}
        />
      </Box>
      <Box component="div" sx={{ display: 'flex', gap: 0.5 }}>
        <YamlEditor
          open={open}
          onClose={() => { setOpen(false); }}
          fileName={fileName}
        />
        <CustomButton
          size="small"
          onClick={handleOpenEditor}
          startIcon={<EditIcon />}
          variant="contained"
          sx={{ fontSize: '10px', width: '100%' }}
        >
          Edit
        </CustomButton>
        <CustomButton
          size="small"
          onClick={onExportClick}
          startIcon={<DownloadIcon />}
          variant="contained"
          sx={{ fontSize: '10px', width: '100%' }}
        >
          Export File
        </CustomButton>
        <CustomButton
          size="small"
          onClick={onImportClick}
          startIcon={<UploadIcon />}
          variant="contained"
          sx={{ fontSize: '10px', width: '100%' }}
        >
          Import File
        </CustomButton>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".yml,.yaml"
          onChange={(e) =>
            importFile(
              e,
              allModels,
              setDisplayModel,
              setAppConfig,
              setCFDConfig,
              setParameterList,
            )
          }
        />
      </Box>
    </Box >
  );
};
